/**
 * SettingsPage — platform settings for authenticated users.
 *
 * Tabbed layout with four panels:
 * - API Tokens (default)
 * - Exchange Keys
 * - Risk Controls
 * - Notifications
 *
 * Active tab is controlled by settingsStore so it can be deep-set from elsewhere.
 */

import { useAuthStore } from '../../stores/authStore';
import { useSettingsStore } from '../../stores/settingsStore';
import type { SettingsTab } from '../../stores/settingsStore';
import { CredentialsPanel } from '../CredentialsPanel/CredentialsPanel';
import { KillSwitchPanel } from '../PaperTradingPage/KillSwitchPanel';
import { ApiTokensPanel } from '../ApiTokensPanel/ApiTokensPanel';
import { NotificationsPanel } from '../NotificationsPanel/NotificationsPanel';

const TABS: Array<{ id: SettingsTab; label: string }> = [
  { id: 'apiTokens', label: 'API Tokens' },
  { id: 'exchangeKeys', label: 'Exchange Keys' },
  { id: 'riskControls', label: 'Risk Controls' },
  { id: 'notifications', label: 'Notifications' },
];

export function SettingsPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const activeTab = useSettingsStore((s) => s.activeTab);
  const setActiveTab = useSettingsStore((s) => s.setActiveTab);

  if (!isAuthenticated) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <svg
            className="w-12 h-12 mx-auto mb-4 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          <p className="text-gray-300 font-medium">Please log in to access settings</p>
          <p className="text-gray-500 text-sm mt-1">
            Settings are only available to authenticated users.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {/* Page heading */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-white">Settings</h1>
        <p className="text-sm text-gray-400 mt-1">
          Manage API tokens, exchange keys, and risk controls for the trading platform.
        </p>
      </div>

      {/* Tab bar — styled like StrategyConfig */}
      <div className="flex border-b border-gray-700 mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary-500 text-white'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Panel content */}
      {activeTab === 'apiTokens' && <ApiTokensPanel />}
      {activeTab === 'exchangeKeys' && <CredentialsPanel />}
      {activeTab === 'riskControls' && <KillSwitchPanel />}
      {activeTab === 'notifications' && <NotificationsPanel />}
    </div>
  );
}
